/**
 * A tela de Empresas (história 2.6).
 *
 *     /empresas?busca=&pagina=    a listagem da base comercial
 *
 * Ela é filha de `rotaDaAplicacao`, e por isso herda a guarda de sessão do layout — registrada
 * uma vez lá, ela vale para toda rota interna.
 *
 * **O caminho continua vindo de `CAMINHOS`.** O que mudou na 2.6 foi só quem desenha o corpo:
 * `/empresas` saiu do laço de placeholders de `telas.ts` (via `COM_TELA_PROPRIA`) e ganhou
 * arquivo próprio, como `/administracao` fez na 2.3. Item de menu e rota continuam nascendo da
 * **mesma** lista.
 *
 * **A busca e a página moram na URL, e não num `useState`.** Elas são o "onde eu estou" do
 * operador, e é isso que o README do backend usa para justificar `pagina`/`limite` contra o
 * cursor: quem pagina quer saber em que ponto do alfabeto está **e voltar**. Em estado local
 * elas se perdem em três situações reais — recarregar a aba, abrir uma ficha e voltar, e
 * atravessar os 768px do shell (girar o aparelho, redimensionar a janela), que troca a árvore e
 * remonta o `<Outlet/>`. Na URL elas sobrevivem às três, viram link compartilhável, e o botão
 * "voltar" do navegador desfaz uma paginação como qualquer pessoa espera.
 *
 * **A rota existe para todos os perfis.** Quem recorta o que cada um enxerga é a RLS do serviço,
 * linha a linha (FR-30, "é responsável, vê") — e não a rota: um Vendedor abre esta tela e recebe
 * a carteira dele, e não um `403`.
 *
 * **Sem cabeçalho próprio.** O `Cabecalho` do shell já desenha o título da tela ("Empresas");
 * o cartão desta tela tem nome **distinto** ("Base comercial"), como as "Listas de domínio" da
 * 2.3 sob o título "Administração" — dois títulos iguais na mesma página seriam dois marcos
 * indistinguíveis para quem navega por leitor de tela. O **subtítulo** do cabeçalho é que vem
 * da tela, por `useDefinirSubtitulo`: as contagens só existem depois da resposta do serviço.
 *
 * `/empresas/nova` e `/empresas/$empresaId` (2.1) são caminhos **irmãos** deste na árvore, e não
 * filhos: `/empresas` não é um layout com `<Outlet/>`.
 */

import { createRoute } from '@tanstack/react-router'

import { ListaDeEmpresas } from '@/modulos/cadastro/ListaDeEmpresas'
import { PAGINA_MAXIMA, TAMANHO_DA_BUSCA } from '@/modulos/cadastro/api'
import { CAMINHOS } from '@/shell/navegacao'
import { rotaDaAplicacao } from './aplicacao'

/** O que a URL desta tela carrega. Os dois campos são opcionais: `/empresas` cru é válido. */
export interface BuscaDaListaDeEmpresas {
  /** O termo digitado. Ausente quando ninguém buscou nada. */
  busca?: string
  /** A página aberta, começando em 1. Ausente é a primeira. */
  pagina?: number
  /** A etiqueta da equipe do filtro (13.6): um `uuid`. Ausente é "todas". */
  etiqueta?: string
}

/** Um `uuid` no formato canônico — o resto que chega em `etiqueta` cai fora (13.6). */
export const UUID_DA_ETIQUETA = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A etiqueta da URL, se for um `uuid` (em minúsculas, a forma do catálogo); senão nada — e a tela
 * não manda um `422` cru ao serviço.
 */
export function etiquetaDaUrl(valor: unknown): string | undefined {
  return typeof valor === 'string' && UUID_DA_ETIQUETA.test(valor) ? valor.toLowerCase() : undefined
}

/**
 * A primeira página. Ela é o padrão de `pagina` e o destino de todo termo novo.
 *
 * Escrita uma vez porque três lugares a usam — o validador, a tela ao trocar o termo e a
 * correção de página fora da faixa —, e um `1` solto em cada um deles é um lugar a mais para
 * alguém trocar por engano.
 */
export const PRIMEIRA_PAGINA = 1

function Empresas() {
  // A anotação é explícita porque `useSearch` da própria rota que a declara vê o tipo em
  // construção (`Empresas` é o `component` de `rotaEmpresas`), e a inferência cai numa união
  // com o `{}` do layout pai. `validateSearch` continua sendo o dono do formato.
  const { busca, pagina, etiqueta }: BuscaDaListaDeEmpresas = rotaEmpresas.useSearch()
  const navegar = rotaEmpresas.useNavigate()

  return (
    <ListaDeEmpresas
      busca={busca ?? ''}
      pagina={pagina ?? PRIMEIRA_PAGINA}
      etiqueta={etiqueta}
      /*
       * Digitar **substitui** a entrada de histórico, e paginar **empurra** uma nova.
       *
       * São duas intenções diferentes: ninguém quer que o botão "voltar" desfaça uma letra de
       * cada vez, e todo mundo espera que ele desfaça uma virada de página — com uma exceção, a
       * correção automática de página fora da faixa, que pede `substituir`. Trocar o termo
       * também zera `pagina` — a página 4 de um resultado antigo não existe no novo, e ficar
       * nela devolveria uma lista vazia com o campo preenchido.
       */
      aoTrocarABusca={(termo) => {
        void navegar({
          // A forma de função, desde a 13.6: o filtro por etiqueta fica, e só a página zera.
          search: (atual) => ({
            ...atual,
            busca: termo === '' ? undefined : termo,
            pagina: undefined,
          }),
          // O *state* do aviso de apagar (13.6, `usoDaEtiqueta`) atravessa a digitação e a página.
          state: true,
          replace: true,
        })
      }}
      aoTrocarAEtiqueta={(etiquetaId) => {
        void navegar({
          // Trocar o filtro zera a página: a página 4 de outra etiqueta não existe nesta.
          search: (atual) => ({ ...atual, etiqueta: etiquetaId, pagina: undefined }),
        })
      }}
      aoTrocarAPagina={(destino, opcoes) => {
        void navegar({
          // A forma de função **funde** com o que já está na URL, e é o que preserva `busca` e
          // `etiqueta` ao virar de página — a forma de objeto trocaria a busca inteira.
          search: (atual) => ({
            ...atual,
            pagina: destino === PRIMEIRA_PAGINA ? undefined : destino,
          }),
          // Só a correção de uma página que não existe mais substitui: ver a nota em
          // `PropsDaListaDeEmpresas.aoTrocarAPagina`.
          state: true,
          replace: opcoes?.substituir === true,
        })
      }}
    />
  )
}

export const rotaEmpresas = createRoute({
  getParentRoute: () => rotaDaAplicacao,
  path: CAMINHOS.empresas,
  component: Empresas,
  /**
   * A URL é entrada de quem chega, e não só do que a tela escreveu: alguém edita a barra de
   * endereço, um link antigo circula, o histórico traz um valor de outra versão da tela. Tudo
   * o que não for um par válido cai fora **aqui**, e a tela recebe sempre um estado que ela
   * sabe desenhar — em vez de mandar `?pagina=abc` ao serviço e receber um `422` cru.
   *
   * Os dois tetos são os **do serviço** (`TAMANHO_DA_BUSCA`, `PAGINA_MAXIMA`): aplicá-los aqui
   * evita a ida à rede que voltaria em `422`, e o campo da tela usa o primeiro no `maxLength`.
   *
   * O termo **não** é aparado aqui, de propósito: ele é o valor do `<input>`, e aparar a cada
   * tecla impediria a pessoa de digitar um espaço no meio de "Vale Verde". Quem apara é
   * `normalizarParametros`, na hora de montar a consulta — e lá termo só de espaço vira termo
   * ausente, que é o que o serviço já faz com ele.
   */
  validateSearch: (parametros: Record<string, unknown>): BuscaDaListaDeEmpresas => {
    const termo = typeof parametros.busca === 'string' ? parametros.busca : ''
    const pagina = Number(parametros.pagina)
    return {
      etiqueta: etiquetaDaUrl(parametros.etiqueta),
      busca: termo === '' ? undefined : termo.slice(0, TAMANHO_DA_BUSCA),
      // `Number.isSafeInteger` recusa `abc` (NaN), `1.5` e `Infinity`; `PAGINA_MAXIMA` recusa o
      // inteiro grande e válido, que passaria por aqui e voltaria do serviço em `422` — o teto
      // é dele, e este é o espelho. A primeira página fica **fora** da URL: `/empresas` e
      // `/empresas?pagina=1` são o mesmo lugar, e devem ter o mesmo endereço.
      pagina:
        Number.isSafeInteger(pagina) && pagina > PRIMEIRA_PAGINA && pagina <= PAGINA_MAXIMA
          ? pagina
          : undefined,
    }
  },
})
